/* eslint-disable @typescript-eslint/no-explicit-any */
import * as echarts from "echarts";
import React, { useEffect, useRef } from "react";
import {
  getMonthName,
  getLastTwelveMonthIndices,
} from "src/helpers/api/sharedFunction";
import { RsiPoint } from "src/helpers";
import moment from "moment-timezone";

type Props = {
  rsiData: RsiPoint[];
  darkMode: boolean;
  isExpand?: boolean;
  isOrderblock?: boolean;
};

const seriesConfigB = [
  {
    name: "Daily",
    key: "rsi_daily",
    color: "#19C37D",
    shadowDark: "rgba(25, 195, 125, 0.6)",
    shadowLight: "rgba(0, 128, 64, 0.4)",
    emphasisShadow: "rgba(25, 195, 125, 0.9)",
  },
  {
    name: "Weekly",
    key: "rsi_weekly",
    color: "#FA7B17",
    shadowDark: "rgba(250, 123, 23, 0.6)",
    shadowLight: "rgba(150, 70, 10, 0.4)",
    emphasisShadow: "rgba(250, 123, 23, 0.9)",
  },
  {
    name: "Monthly",
    key: "rsi_monthly",
    color: "#FD1050",
    shadowDark: "rgba(253, 16, 80, 0.6)",
    shadowLight: "rgba(180, 0, 50, 0.4)",
    emphasisShadow: "rgba(253, 16, 80, 0.9)",
  },
];

const showEveryB = 6;

const RSILineChart: React.FC<Props> = ({
  rsiData,
  darkMode,
  isExpand,
  isOrderblock,
}) => {
  const chartRef = useRef<HTMLDivElement | null>(null);
  const chart = useRef<echarts.EChartsType | null>(null);

  const dates = rsiData.map((item) => {
    return moment.utc(item?.date).tz("Asia/Kolkata").format("YYYY-MM-DD");
  });

  // Scope legend preference dynamically based on the current module's route
  const pathname =
    typeof window !== "undefined" ? window.location.pathname : "";
  const moduleName = pathname.split("/")[1] || "default";

  // Single global key — legend preference applies to all stocks
  const LEGEND_STORAGE_KEY = `rsi_legend_selected_${moduleName}`;
  const LEGEND_SYNC_EVENT = `rsi-legend-changed-${moduleName}`;

  const getSavedLegendSelected = (): Record<string, boolean> => {
    try {
      const saved = localStorage.getItem(LEGEND_STORAGE_KEY);
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  };

  const saveLegendSelected = (selected: Record<string, boolean>) => {
    try {
      localStorage.setItem(LEGEND_STORAGE_KEY, JSON.stringify(selected));
      // Notify the sibling chart instance (card ↔ expand modal)
      window.dispatchEvent(
        new CustomEvent(LEGEND_SYNC_EVENT, { detail: selected }),
      );
    } catch {
      // ignore storage errors
    }
  };

  const series = seriesConfigB.map((cfg) => ({
    name: cfg.name,
    type: "line",
    color: cfg.color,
    smooth: true,
    data: rsiData.map((d) => (d as any)[cfg.key]),
    symbol: "circle",
    symbolSize: (params: { dataIndex: number }) =>
      params.dataIndex % 12 === 0 ? 4 : 0,
    lineStyle: {
      width: 1.5,
      shadowColor: darkMode ? cfg.shadowDark : cfg.shadowLight,
      shadowBlur: 10,
      shadowOffsetX: 2,
      shadowOffsetY: 2,
    },
    emphasis: {
      focus: "series",
      lineStyle: {
        width: 2,
        shadowColor: cfg.emphasisShadow,
        shadowBlur: 20,
      },
    },
    label: {
      show: true,
      position: "top",
      distance: 12,
      offset: [0, -10],
      formatter: (params: { dataIndex: number; data: number }) =>
        params.dataIndex % showEveryB === 0 ? `${Math.round(params.data)}` : "",
      fontSize: 11,
      color: cfg.color,
      padding: [2, 6],
      borderRadius: 4,
      overflow: "truncate" as const,
    },
    labelLayout: {
      hideOverlap: true,
    },
  }));

  const lastDate = new Date(dates[dates.length - 1] ?? Date.now());
  const fiveMonthsAgo = new Date(lastDate);
  fiveMonthsAgo.setMonth(lastDate.getMonth() - 5);
  let startIndex = dates.findIndex((d) => new Date(d) >= fiveMonthsAgo);
  if (startIndex === -1) startIndex = 0;
  const start = (startIndex / Math.max(1, dates.length)) * 100;

  const totalStart = dates.length > 0 ? new Date(dates[0]).getTime() : 0;
  const totalEnd =
    dates.length > 0 ? new Date(dates[dates.length - 1]).getTime() : 0;
  const totalDuration = totalEnd - totalStart;
  const twoMonths = 1000 * 60 * 60 * 24 * 30 * 2; // approx 2 months
  const minSpanPercentage =
    totalDuration > 0 ? (twoMonths / totalDuration) * 100 : 17;

  const selectedIndices = getLastTwelveMonthIndices(dates);

  const defaultLegendSelected: Record<string, boolean> = {
    Daily: true,
    Weekly: true,
    Monthly: true,
  };
  const savedLegend = getSavedLegendSelected();
  const mergedLegendSelected = { ...defaultLegendSelected, ...savedLegend };

  const option = {
    backgroundColor: "transparent",
    tooltip: {
      trigger: "axis" as const,
      axisPointer: { type: "cross" as const },
      formatter: function (
        params: {
          axisValue: string;
          seriesType: string;
          color: string;
          seriesName: string;
          data: number;
        }[],
      ) {
        const dateStr = params[0]?.axisValue;
        const dateIST = moment
          .tz(dateStr, "Asia/Kolkata")
          .format("DD MMM YYYY");

        let tooltipHtml = `<div>${dateIST}</div>`;

        params.forEach((param) => {
          if (param.seriesType === "line") {
            tooltipHtml += `
        <div>
          <span style="color:${param.color}; margin-right:6px;">●</span>
          ${param.seriesName}: ${Math.round(param.data)}
        </div>`;
          }
        });
        return tooltipHtml;
      },
    },
    legend: {
      data: seriesConfigB.map((s) => s.name),
      selected: mergedLegendSelected,
      top: 0,
      itemWidth: 20,
      itemHeight: 9,
      left: "center",
    },
    grid: [{ left: 17, right: 15, bottom: 10, containLabel: true }],
    xAxis: {
      type: "category" as const,
      data: dates,
      axisLabel: {
        formatter: function (value: string) {
          const date = new Date(value);
          return getMonthName(date);
        },
        interval: function (index: number) {
          return selectedIndices.includes(index);
        },
        hideOverlap: true,
      },
      axisPointer: {
        label: {
          formatter: function (params: { value: string }) {
            const date = new Date(params.value);
            const day = date.getDate().toString().padStart(2, "0");
            const month = date.toLocaleString("default", { month: "short" });
            const year = date.getFullYear();
            return `${day} ${month} ${year}`;
          },
        },
      },
    },
    yAxis: {
      type: "value" as const,
      scale: true,
      splitLine: { show: false },
      axisLabel: { show: false },
    },
    dataZoom: [
      {
        type: "inside" as const,
        xAxisIndex: [0],
        start,
        end: 100,
        minSpan: minSpanPercentage,
        zoomLock: isExpand ? false : true,
      },
      {
        show: false,
        xAxisIndex: [0],
        minSpan: minSpanPercentage,
        type: "slider",
        zoomLock: isExpand ? false : true,
        start,
        end: 100,
        labelFormatter: function (value: number) {
          const rawDate = dates[value];
          if (!rawDate) return "";
          const date = new Date(rawDate);
          const day = date.getDate().toString().padStart(2, "0");
          const month = date.toLocaleString("default", { month: "short" });
          const year = date.getFullYear();
          return `${day} ${month} ${year}`;
        },
      },
    ],
    series,
  };

  useEffect(() => {
    if (!chartRef.current) return;
    if (!chart.current) {
      chart.current = echarts.init(
        chartRef.current,
        darkMode ? "dark" : "light",
      );

      // Persist legend selections to localStorage
      chart.current.on("legendselectchanged", (params: unknown) => {
        const legendEvent = params as {
          type: string;
          name: string;
          selected: Record<string, boolean>;
        };
        saveLegendSelected(legendEvent.selected);
      });
    }
    // notMerge=false so legend selection isn't wiped on data updates
    chart.current.setOption(option, false);

    // Sync legend from the sibling chart instance (card ↔ expand modal)
    const handleLegendSync = (e: Event) => {
      const selected = (e as CustomEvent<Record<string, boolean>>).detail;
      chart.current?.setOption({ legend: { selected } }, false);
    };
    window.addEventListener(LEGEND_SYNC_EVENT, handleLegendSync);

    const onResize = () => chart.current?.resize();
    window.addEventListener("resize", onResize);

    return () => {
      window.removeEventListener(LEGEND_SYNC_EVENT, handleLegendSync);
      window.removeEventListener("resize", onResize);
      if (chart.current) {
        chart.current.dispose();
        chart.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rsiData, darkMode, isExpand, isOrderblock]);

  return (
    <div
      ref={chartRef}
      id="rsi-chart"
      className={`w-full ${isExpand ? "h-full" : isOrderblock ? "h-full" : "h-full"}`}
      style={{ transform: "scale(0.98)" }}
    />
  );
};

export default React.memo(RSILineChart);
